import type { ChangeEvent } from "react";
import type { LlHlsSettings } from "../llHls.mjs";
import { Stack, TextField, MenuItem, Typography } from "@mui/material";
import { normalizeLlHls } from "../llHls.mjs";

export default function LlHlsParams({ value, onChange, disabled }: { value: LlHlsSettings; onChange: (value: LlHlsSettings) => void; disabled?: boolean }) {
  const settings = normalizeLlHls(value);
  let playbackAddress: URL | undefined;
  try { playbackAddress = new URL(String(settings.hlsPlaybackUrl).trim()); } catch { /* Show saved settings until the URL is valid. */ }
  const displayValue = (key: string) => playbackAddress && key === "hlsAdvertisedHost" ? playbackAddress.hostname
    : playbackAddress && key === "hlsPort" ? (playbackAddress.port || (playbackAddress.protocol === "https:" ? 443 : 80)) : settings[key];
  const change = (key: string) => (event: ChangeEvent<HTMLInputElement>) => onChange({ ...settings, [key]: event.target.value });
  return <Stack spacing={1.5}>
    <TextField select label="Segment format" value={settings.hlsSegmentFormat} onChange={change("hlsSegmentFormat")} disabled={disabled} size="small">
      <MenuItem value="mpegts">MPEG-TS</MenuItem><MenuItem value="mp4">Fragmented MP4 (CMAF)</MenuItem>
    </TextField>
    <Typography variant="body2" color="text.secondary">
      {settings.hlsSegmentFormat === "mp4" ? "KLV is delivered separately with media timestamps. The receiver needs STView sidecar support." : "KLV remains embedded in the transport stream."}
    </Typography>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      {[ ["hlsBindAddress", "Listen address", 1], ["hlsAdvertisedHost", "Playback hostname / IP (optional)", 1.5],
        ["hlsPort", "Media port (0 = automatic)", 1] ].map(([key, label, flex]) =>
        <TextField key={key} size="small" label={label} value={displayValue(String(key))} onChange={change(String(key))} disabled={disabled || (Boolean(String(settings.hlsPlaybackUrl).trim()) && ["hlsAdvertisedHost", "hlsPort"].includes(String(key)))}
          helperText={String(settings.hlsPlaybackUrl).trim() && ["hlsAdvertisedHost", "hlsPort"].includes(String(key)) ? "Set by Playback URL" : undefined}
          sx={{ flex, minWidth: 0 }} />)}
    </Stack>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      {[ ["hlsSegmentDurationMs", "Segment duration (ms)"], ["hlsPartDurationMs", "Part duration (ms)"],
        ["hlsWindowSegments", "Live window (segments)"] ].map(([key, label]) =>
        <TextField key={key} size="small" label={label} value={settings[key]} onChange={change(String(key))} disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }} />)}
    </Stack>
    {[ ["hlsTlsCertificatePath", "TLS certificate path (optional)"], ["hlsTlsKeyPath", "TLS private key path (optional)"] ].map(([key, label]) =>
      <TextField key={key} size="small" label={label} value={settings[key]} onChange={change(String(key))} disabled={disabled} />)}
  </Stack>;
}

